import React, {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { type TableSelection } from "@devexpress/dx-react-grid-bootstrap4";
import { type Dimensions } from "@features/sales-v3/charts/chart";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { useActiveTab } from "@features/sales-v3/hooks/use-active-tab";
import { useTabState } from "@features/sales-v3/hooks/use-tab-state";
import { useSplitView } from "@features/sales-v3/hooks/use-table-selection";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { CustomCheckbox } from "@shared/custom-checkbox";

import { isKeyParent, isParent } from "../table";

export const TableSelectionCell = ({
  selected,
  row,
  onToggle,
  tableRow,
  tableColumn,
  ...restOfProps
}: PropsWithChildren<TableSelection.CellProps>) => {
  const { activeTab } = useActiveTab();
  const { tabState } = useTabState(activeTab);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const isSplitView = useSplitView();
  const { breakdowns } = usePageConfiguration();
  const { designElementManager } = useDesignElementManager();

  const selectColor =
    isSplitView && selected
      ? designElementManager.getDesignElementColor(row.rowMetadata.naming.name)
      : "#7081FF"; // cosmic
  const currentDepth = breakdowns.findIndex(
    (b) => b.key === row.rowMetadata.breakdown,
  );
  const parsedKey = JSON.parse(row.key);
  const noBreakdowns = Array.isArray(parsedKey)
    ? (parsedKey as Dimensions[]).every((k) => !Object.keys(k).length) ||
      (breakdowns.length > 0 &&
        currentDepth !== Object.keys(row.rowMetadata.dimensions).length - 1)
    : false;
  const canShowSelect =
    !isSplitView || (isSplitView && !tableRow.row.hasChildren);

  const isIntermediate = useMemo(() => {
    if (!Array.isArray(JSON.parse(row.key))) {
      return false;
    }

    const result =
      tabState.proposedFiltersState.inclusionAtomicities.some((atom) =>
        isParent(row.key, atom.atomicity),
      ) ||
      tabState.proposedFiltersState.inclusionAdKeys.some((adKey) =>
        isKeyParent(adKey.dimensions, row.key),
      ) ||
      tabState.proposedFiltersState.exclusionAdKeys.some((adKey) =>
        isKeyParent(adKey.dimensions, row.key),
      ) ||
      tabState.proposedFiltersState.exclusionAtomicities.some((atom) =>
        isParent(row.key, atom.atomicity),
      );
    return result;
  }, [
    row.key,
    tabState.proposedFiltersState.exclusionAdKeys,
    tabState.proposedFiltersState.exclusionAtomicities,
    tabState.proposedFiltersState.inclusionAdKeys,
    tabState.proposedFiltersState.inclusionAtomicities,
  ]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIntermediate;
    }
  }, [isIntermediate]);

  return (
    <td className="sticky left-0 " {...restOfProps}>
      <div
        className="flex items-center justify-center w-full h-full"
        onClick={() => onToggle()}
      >
        {canShowSelect && !noBreakdowns && (
          <CustomCheckbox
            className="pointer-events-none"
            ref={checkboxRef}
            backgroundColor={selectColor}
            checked={selected}
          />
        )}
      </div>
    </td>
  );
};
